import {useCallback, useLayoutEffect, useRef} from 'react';

const SETTLE_EASE = 'cubic-bezier(.22,1,.36,1)';
const RISE_MS = 620;
const GHOST_MS = 680;

const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function cloneAsGhost(node: HTMLElement) {
  const rect = node.getBoundingClientRect();
  const ghost = node.cloneNode(true) as HTMLElement;
  ghost.setAttribute('aria-hidden', 'true');
  ghost.setAttribute('inert', '');
  ghost.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
  Object.assign(ghost.style, {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
  });
  return ghost;
}

function useStepTransition<Step>(step: Step) {
  const columnRef = useRef<HTMLDivElement>(null);
  const ghostLayerRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLElement | null>(null);
  const previousStepRef = useRef(step);

  const captureCurrentStep = useCallback(() => {
    ghostRef.current =
      columnRef.current && !prefersReducedMotion()
        ? cloneAsGhost(columnRef.current)
        : null;
  }, []);

  useLayoutEffect(
    function animateStepChange() {
      const previousStep = previousStepRef.current;
      if (previousStep === step) return;
      previousStepRef.current = step;
      if (previousStep === null) return;

      const column = columnRef.current;
      column
        ?.querySelector<HTMLElement>('[data-step-heading]')
        ?.focus({preventScroll: true});

      const ghost = ghostRef.current;
      ghostRef.current = null;
      if (!column || !ghost || !ghostLayerRef.current) return;

      ghostLayerRef.current.appendChild(ghost);
      const timeout = window.setTimeout(() => ghost.remove(), GHOST_MS);
      const cleanUp = () => {
        window.clearTimeout(timeout);
        ghost.remove();
      };

      if (isDesktop()) {
        ghost.animate(
          [
            {opacity: 1, transform: 'none'},
            {opacity: 0, transform: 'translateX(-48px)'},
          ],
          {duration: 220, easing: 'ease-in', fill: 'forwards'},
        );
        column.animate(
          [
            {opacity: 0, transform: 'translateX(48px)'},
            {opacity: 1, transform: 'none'},
          ],
          {duration: 380, delay: 120, easing: SETTLE_EASE, fill: 'backwards'},
        );
        return cleanUp;
      }

      const rise = Math.max(0, window.innerHeight - column.getBoundingClientRect().top);
      ghost.animate([{opacity: 1}, {opacity: 0.5}], {
        duration: RISE_MS,
        fill: 'forwards',
      });
      column.animate(
        [
          {transform: `translateY(${rise}px)`, boxShadow: 'var(--chekin-shadow-lift-up)'},
          {transform: 'none', boxShadow: 'var(--chekin-shadow-lift-up)'},
        ],
        {duration: RISE_MS, easing: SETTLE_EASE},
      );
      return cleanUp;
    },
    [step],
  );

  return {columnRef, ghostLayerRef, captureCurrentStep};
}

export {useStepTransition};
